import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level render boundary.
 *
 * React has no hook equivalent — `componentDidCatch` is class-only, which is
 * the entire reason this file is not a function component.
 *
 * Reporting happens HERE and nowhere else, so the fallback UI is UI only. A
 * fallback that logs on render double-counts every error, and triple-counts it
 * after the user taps retry.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("character-studio.render_crash", {
      message: error.message,
      componentStack: info.componentStack,
      release: import.meta.env.VITE_RELEASE_ID ?? "dev",
    });
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return this.props.fallback(error, () => this.setState({ error: null }));
  }
}
